<template>
    <v-container>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-row >
                        <v-col cols="12" v-if="loading" class="notPrinted">
                            <v-progress-linear :indeterminate="true"></v-progress-linear>
                        </v-col>

                        <v-col cols="12" v-if="!loading && loadError" class="notPrinted">
                            <v-alert outlined :value="true" color="error" icon="error">
                                {{loadError}}
                            </v-alert>
                        </v-col>

                        <v-col cols="12" v-if="!loading && !loadError">
                            <v-row class="notPrinted">
                                <v-col cols="12">
                                    <h2 class="text-center">Auditing</h2>
                                </v-col>
                            </v-row>
                            <v-form @submit.prevent v-model="searchFormValid" class="notPrinted">
                                <v-row >
                                    <v-col cols="12" md="3" offset-md="2">
                                        <v-autocomplete
                                            :items="users"
                                            item-text="Email"
                                            item-value="Email"
                                            v-model="search.userEmails"
                                            multiple
                                            label="Users"
                                            hint="Users"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="12" md="3" offset-md="2">
                                        <v-text-field
                                            clearable
                                            v-model="search.snakeNumbers"
                                            label="Specimen IDs">
                                        </v-text-field>
                                    </v-col>
                                </v-row>
                                <v-row >
                                    <v-col cols="12" sm="6" md="3" offset-md="2">
                                        <v-menu
                                            ref="datePickerMenu"
                                            :close-on-content-click="false"
                                            v-model="startDatePickerMenu"
                                            :nudge-right="40"
                                            transition="scale-transition"
                                            offset-y
                                            min-width="290px">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    clearable
                                                    v-model="displayMinDate"
                                                    label="Start Date"
                                                    prepend-icon="event"
                                                    readonly
                                                    v-bind="attrs"
                                                    v-on="on"
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker 
                                                :allowed-dates="allowedDates"
                                                v-model="search.minDate"
                                                @input="startDatePickerMenu = false"
                                                :title-date-format="datePickerFormat"
                                            >
                                            </v-date-picker>
                                        </v-menu>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="3" offset-md="2">
                                        <v-menu
                                            ref="datePickerMenu"
                                            :close-on-content-click="false"
                                            v-model="endDatePickerMenu"
                                            :nudge-right="40"
                                            transition="scale-transition"
                                            offset-y
                                            min-width="290px">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    clearable
                                                    v-model="displayMaxDate"
                                                    label="End Date"
                                                    prepend-icon="event"
                                                    readonly
                                                    v-bind="attrs"
                                                    v-on="on"
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker 
                                                :allowed-dates="allowedDates"
                                                v-model="search.maxDate"
                                                @input="endDatePickerMenu = false"
                                                :title-date-format="datePickerFormat"
                                            >
                                            </v-date-picker>
                                        </v-menu>
                                    </v-col>
                                    <v-col cols="12" md="8" offset-md="2">
                                        <v-btn block color="primary" :disabled="!searchFormValid" :loading="loadingAuditData" @click="loadAuditData">Search</v-btn>
                                    </v-col>
                                </v-row>
                            </v-form>
                            <v-row >
                                <v-col cols="12">
                                    <v-data-table
                                        :headers="headers"
                                        :items="auditData"
                                        :loading="loadingAuditData"
                                        item-key="AuditID"
                                        :server-items-length="auditDataTotal"
                                        :footer-props="footerProps"
                                        :sort-by.sync="auditDataPagination.sortBy"
                                        :sort-desc.sync="auditDataPagination.descending"
                                        :items-per-page.sync="auditDataPagination.rowsPerPage"
                                        :page.sync="auditDataPagination.page"
                                        class="audit-data-table"
                                    >
                                        <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>

                                        <template v-slot:headers="props">
                                            <tr class="print-header">
                                                <th colspan="7">
                                                    <v-row >
                                                        <v-col cols="1" class="text-right">
                                                            <img :src="iconURL" height="20" class="home-logo" />
                                                        </v-col>
                                                        <v-col cols="3" class="text-left">
                                                            <span class="print-text">Audit Report</span>
                                                        </v-col>
                                                        <v-col cols="8" class="text-right">
                                                            <span class="print-text">Generated by {{currentUserName}} {{reportGeneratedTime}}</span>
                                                        </v-col>
                                                    </v-row>
                                                </th>
                                            </tr>
                                            <tr>
                                                <th
                                                    v-for="header in props.headers"
                                                    :key="header.text"
                                                    :class="['column sortable', auditDataPagination.descending ? 'desc' : 'asc', header.value === auditDataPagination.sortBy ? 'active' : '', 'text-xs-left']"
                                                    @click="changeSort(header.value)"
                                                >
                                                    {{ header.text }}
                                                    <v-icon small>arrow_upward</v-icon>
                                                </th>
                                            </tr>
                                        </template>

                                        <template v-slot:item="props">
                                            <tr :data-id="props.item.AuditID" @click="props.expanded = !props.expanded" class="audit-data-row grey lighten-2">
                                                <td>{{props.item.AuditID}}</td>
                                                <td>{{props.item.AuditedTable}}</td>
                                                <td>{{props.item.SpecimenDisplayID}}</td>
                                                <td>{{props.item.AuditTypeName}}</td>
                                                <td>{{props.item.FirstName}} {{props.item.LastName}}</td>
                                                <td>{{formatAuditTime(props.item.OccurredAt)}}</td>
                                                <td>{{props.item.AuditMessage}}</td>
                                            </tr>
                                            <tr v-if="props.item.AuditValues" class="audit-values-row">
                                                <td colspan="1"></td>
                                                <td colspan="6" class="audit-values-table-column">
                                                    <v-data-table
                                                        :headers="auditValuesHeaders"
                                                        :items="props.item.AuditValues"
                                                        item-key="AuditValueID"
                                                        hide-default-footer
                                                        :items-per-page="-1"
                                                        class="audit-values-table"
                                                    >
                                                        <template v-slot:item="auditValues">
                                                            <tr :data-id="props.item.AuditID">
                                                                <td>{{auditValues.item.AffectedColumn}}</td>
                                                                <td>{{formatAuditValue(auditValues.item.OldValueFriendly)}}</td>
                                                                <td>{{formatAuditValue(auditValues.item.NewValueFriendly)}}</td>
                                                            </tr>
                                                        </template>
                                                    </v-data-table>
                                                </td>
                                            </tr>
                                        </template>
                                        <template v-if="!loadingAuditData" slot="no-data">
                                            <v-alert v-if="loadAuditDataError" outline :value="true" color="error" icon="error">
                                                {{loadAuditDataError}}
                                            </v-alert>
                                            <v-alert v-else outlined :value="true" type="info">
                                                No results found
                                            </v-alert>
                                        </template>
                                    </v-data-table>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import util from '@/helpers/util';
import BTGIcon from "../../assets/img/btg_logo.svg"
import constants from 'comunet-btg-utah-repository-shared/constants';

import moment from 'moment';

export default {
    components: {
    },
    props: [
        
    ],
    data() {
        return {
            searchFormValid: false,
            search: {
                userEmails: [],
                minDate: null,
                maxDate: null,
                snakeNumbers: ""
            },
            startDatePickerMenu: false,
            endDatePickerMenu: false,
            auditDataPagination: {
                rowsPerPage: 10,
                sortBy: "OccurredAt",
                descending: true,
                page: 1
            },
            footerProps: {
                'items-per-page-options': [
                        10,
                        25,
                        100,
                        {"text":"$vuetify.dataIterator.rowsPerPageAll","value":-1}]
            },
            headers: [
                {
                    text: "#",
                    value: "AuditID"
                },
                {
                    text: "Data",
                    value: "AuditedTable",
                },
                {
                    text: "Specimen",
                    value: "SpecimenDisplayID",
                    width: "300px"
                },
                {
                    text: "Type",
                    value: "AuditTypeName",
                    width: "300px"
                },
                {
                    text: "User",
                    value: "Email",
                    width: "300px"
                },
                {
                    text: "Time",
                    value: "OccurredAt",
                     width: "300px"
                },
                {
                    text: "Message",
                    value: "AuditMessage",
                    sortable: false
                }
            ],
            auditValuesHeaders: [
                {
                    text: "Column",
                    value: "AffectedColumn",
                    sortable: false,
                    width: "20%"
                },
                {
                    text: "Old Value",
                    value: "OldValueFriendly",
                    sortable: false,
                    width: "40%"
                },
                {
                    text: "New Value",
                    value: "NewValueFriendly",
                    sortable: false,
                    width: "40%"
                },
            ]
        };
    },
    computed: {
        currentUserName: function () {
            return util.getUserProfileNameFromUserObject(this.$store.state.authStore.user);
        },
        reportGeneratedTime: function() {
            return moment().format("DD/MM/YYYY HH:mm:ss");
        },
        iconURL() {
            return BTGIcon;
        },
        loading() {
            return this.$store.state.userStore.loading;
        },
        loadError() {
            return this.$store.state.userStore.loadError;
        },
        loadingAuditData() {
            return this.$store.state.auditStore.loading;
        },
        loadAuditDataError() {
            return this.$store.state.auditStore.loadError;
        },
        users() {
            // show inactive users because they might have been active in the past
            return [...this.$store.state.userStore.users].sort(util.sortByField("Email"));
        },
        auditData() {
            return this.$store.state.auditStore.auditData;
        },
        auditDataTotal() {
            return this.$store.state.auditStore.auditDataCount;
        },
        displayMinDate: {
            get: function() {
                if (this.search && this.search.minDate) {
                    return moment.utc(this.search.minDate).format(constants.DATE_FORMAT_NO_TIME);
                }
                
                return "";
            },
            set: function(newValue) {
                this.search.minDate = newValue;
            }
        },
        displayMaxDate: {
            get: function() {
                if (this.search && this.search.maxDate) {
                    return moment.utc(this.search.maxDate).format(constants.DATE_FORMAT_NO_TIME);
                }
                
                return "";
            },
            set: function(newValue) {
                this.search.maxDate = newValue;
            }
        }
    },
    methods: {
        formatAuditValue(val) {
            // third argument makes this strict parse, so if it's a date followed by some other text then moment will not say it's valid
            let date = moment.utc(val, constants.MYSQL_DATE_FORMAT, true);
            let dateTime = moment.utc(val, constants.MYSQL_DATE_FORMAT_WITH_TIME, true);
            
            if (date.isValid()) {
                // our value is a date (and nothing else - eg. "2019-01-01 RANDOM BOLLOCKS" is not going to be a valid date here)
                return date.local().format(constants.DATE_FORMAT_NO_TIME);
            }
            else if (dateTime.isValid()) {
                // our valus is a datetime and nothing else
                return dateTime.local().format(constants.DATE_FORMAT);
            }

            return val;
        },
        datePickerFormat(date) {
            return moment.utc(date).format(constants.DATE_FORMAT_NO_TIME);
        },
        changeSort (column) {
            if (this.auditDataPagination.sortBy === column) {
              this.auditDataPagination.descending = !this.auditDataPagination.descending
            }
            else {
                this.auditDataPagination.sortBy = column
                this.auditDataPagination.descending = false
            }
        },
        loadUsers() {
            this.$store.dispatch("userStore/loadUsers");
        },
        allowedDates() {
            // no restriction on allowed dates
            return true;
        },
        clearAuditData() {
            this.$store.dispatch("auditStore/clearAuditData");
        },
        loadAuditData() {
            let params = {
                SearchMinDate: moment(this.search.minDate).utc().format(constants.MYSQL_DATE_FORMAT_WITH_TIME),
                SearchMaxDate: moment(this.search.maxDate).utc().format(constants.MYSQL_DATE_FORMAT_WITH_TIME),
                SearchUserEmails: JSON.stringify(this.search.userEmails),
                SearchSnakeNumbers: this.search.snakeNumbers
            };

            params = Object.assign(params, this.auditDataPagination);

            this.$store.dispatch("auditStore/loadAuditData", {
                params: params
            });
        },
        formatAuditTime(dateString) {
            let otherDate = moment(dateString);

            return otherDate.format(constants.DATE_FORMAT_WITH_SECONDS);
        },
    },
    created() {
        this.loadUsers();
        this.clearAuditData();

        this.loadAuditData();
    },
    watch: {
        auditDataPagination: {
            handler() {
                this.loadAuditData();
            },
            deep: true
        }
    }
};
</script>

<style>
    /* gets rid of an extra border */
    #app .audit-values-row {
        border-bottom: none;
    }

    #app tr.audit-data-row {
        /* do not display border between <tr> elements for the audit records and their audit values */
        border-bottom: none;
    }

    /* this lines up the sub grid first column with the column above it in the main grid */
    #app td.audit-values-table-column {
        padding-left: 0px;
    }

    /* lessen the padding around the sub grid to reduce the amount of room it takes up */
    #app .audit-values-table td,
    #app .audit-values-table th {
        padding-top: 5px;
        padding-bottom: 5px;
        height: 100%;
    }

    @media only screen {
        .print-header {
            display: none;
        }
    }

    @media print {
        .print-header {
            
        }

        .print-text {
            font-size: 13px;
            padding-left: 10px;
        }

        #app table.v-table thead th {
            font-size: 14px;
        }
        
        #app table.v-table tbody td {
            font-size: 12px;
        }

        #app .audit-data-table th,
        #app .audit-data-table td,
        #app .audit-values-table th,
        #app .audit-values-table td {
            padding: 2px;
        }

        #app .audit-data-table th {

        }

        /* hide the icon that display by which column we ordered */
        #app .audit-data-table th i {
            display: none;
        }

        #app .audit-data-table tr {
            /* border-bottom: 1px solid black; */
            -webkit-print-color-adjust:exact;
        }

        #app .audit-values-table {

        }

        #app .audit-values-table th {

        }

        #app .audit-values-table tr {
            
        }

        
    }
</style>